import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Input, Button, Space, Select, Radio, InputNumber, Alert, Spin, message, Tag, Image, Upload, Tabs } from 'antd';
import { PictureOutlined, ThunderboltOutlined, CheckOutlined, ReloadOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { productsApi, metaAdsApi } from '../../utils/api';
import BibliothequeVisuels from './BibliothequeVisuels';
import EditeurCompositionAnnonce from './EditeurCompositionAnnonce';
// Modèles et ratios disponibles, avec leur compatibilité croisée
import {
  MODELES_IMAGE,
  MODELE_GPT,
  FORMAT_PAR_DEFAUT,
  formatsPourModele,
  formatCompatible
} from '../../constants/formatsImage';

/**
 * Génération d'un visuel publicitaire à partir du prompt proposé par l'IA.
 *
 * Réutilise la génération d'images du formulaire produit (NanoGPT) :
 *  - productsApi.generateImage  → images en base64, non encore hébergées
 *  - productsApi.saveGeneratedImage → upload vers le stockage et URL publique
 *
 * L'étape de sauvegarde est indispensable : Meta télécharge l'image depuis son URL,
 * il lui faut donc une adresse publique, pas une image encodée en base64.
 *
 * @param {boolean}  open        - Affichage de la fenêtre
 * @param {Object}   ad          - Annonce concernée (angle, imagePrompt, imageUrl)
 * @param {string}   productId   - Produit promu, pour rattacher l'image générée
 * @param {Function} onSelect    - Reçoit l'URL publique de l'image retenue
 * @param {Function} onClose
 */


const { TextArea } = Input;

const GenerateurImageAnnonce = ({ open, ad, productId, countries = ['CI'], onSelect, onClose }) => {
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState(MODELES_IMAGE[0].value);
  const [quality, setQuality] = useState('medium');
  // Ratio du fichier demandé au fournisseur de génération pour ce visuel publicitaire.
  const [aspectRatio, setAspectRatio] = useState(FORMAT_PAR_DEFAUT);
  const [nombreImages, setNombreImages] = useState(2);
  const [imagesReference, setImagesReference] = useState([]);
  const [referenceChoisie, setReferenceChoisie] = useState(null);
  const [resultats, setResultats] = useState([]);
  const [generation, setGeneration] = useState(false);
  // Rédaction du prompt par l'IA : appel dédié, distinct de la génération de campagne
  const [redactionPrompt, setRedactionPrompt] = useState(false);
  const [propositions, setPropositions] = useState([]);
  // Consigne libre pour orienter la mise en scène lors d'une reformulation
  const [directive, setDirective] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);
  // Visuel affiché en grand avant validation (null = aucun aperçu ouvert)
  const [apercu, setApercu] = useState(null);
  // Visuels retenus, dans l'ordre de sélection : le premier est appliqué à l'annonce
  const [selection, setSelection] = useState([]);
  // Onglet courant : choisir un visuel existant, ou en générer un nouveau
  const [onglet, setOnglet] = useState('bibliotheque');

  /**
   * Change le modèle de génération et réaligne le format si nécessaire.
   * Tous les modèles n'acceptent pas les mêmes ratios : le 4:5 n'existe que chez
   * NanoBanana. La sélection est conservée si elle reste valide, sinon remplacée
   * par le ratio supporté le plus proche.
   *
   * @param {string} nouveauModele
   */
  const changerModele = (nouveauModele) => {
    setModel(nouveauModele);
    setAspectRatio(ratio => formatCompatible(ratio, nouveauModele));
  };

  /**
   * Demande à l'IA de rédiger le prompt visuel de cette annonce.
   * Requête dédiée : le modèle ne fait que cela, à partir de l'angle et du brief validé.
   */
  const redigerPrompt = useCallback(async (consigne = '') => {
    try {
      setRedactionPrompt(true);
      const { data } = await metaAdsApi.generateImagePrompt({
        ad: { angle: ad?.angle, headline: ad?.headline, primaryText: ad?.primaryText },
        visualBrief: ad?.visualDirection || '',
        countries,
        count: 2,
        // Vide à la première rédaction, renseignée quand l'utilisateur clique sur Reformuler
        directive: consigne
      });

      const liste = data?.data?.prompts || [];
      setPropositions(liste);
      if (liste.length > 0) setPrompt(liste[0].prompt);
    } catch (error) {
      console.error('Erreur lors de la rédaction du prompt visuel:', error);
      message.error(error.response?.data?.message || "Impossible de rédiger le prompt");
    } finally {
      setRedactionPrompt(false);
    }
  }, [ad, countries]);

  // À l'ouverture : on remet l'écran à zéro et on reprend le prompt existant s'il y en a un.
  // Aucune requête n'est lancée automatiquement : la rédaction ne part que sur clic,
  // pour ne pas consommer un appel IA que l'utilisateur n'a pas demandé.
  const dejaInitialise = useRef(false);

  useEffect(() => {
    if (!open) {
      dejaInitialise.current = false;
      return;
    }
    if (!ad || dejaInitialise.current) return;

    dejaInitialise.current = true;
    setResultats([]);
    setSelection([]);
    setPropositions([]);
    setDirective('');
    setPrompt(ad.imagePrompt || '');
  }, [open, ad]);

  // Charger les images du produit : elles servent de référence visuelle au modèle
  const chargerImagesProduit = useCallback(async () => {
    if (!productId) return;
    try {
      const reponse = await productsApi.getById(productId);
      const produit = reponse?.data;
      if (!produit) return;

      const urls = [
        ...(produit.images || []).map(i => i.image_url),
        ...(produit.generatedImages || []).map(i => i.image_url)
      ].filter(Boolean);

      // Format { type, data } attendu par le service de génération
      const references = [...new Set(urls)].map(url => ({ type: 'url', data: url }));

      // Les images téléversées par l'utilisateur sont conservées
      setImagesReference(precedentes => [
        ...precedentes.filter(r => r.type === 'data_url'),
        ...references
      ]);
    } catch (error) {
      console.error('Erreur lors du chargement des images du produit:', error);
    }
  }, [productId]);

  useEffect(() => {
    if (open) chargerImagesProduit();
  }, [open, chargerImagesProduit]);

  /**
   * Ajoute une image de référence depuis l'ordinateur.
   * Le fichier est lu en base64 : le service accepte une image téléversée
   * au même titre qu'une URL, via le type 'data_url'.
   *
   * @param {File} fichier
   * @returns {boolean} false pour empêcher l'envoi automatique d'Ant Design
   */
  const televerserReference = (fichier) => {
    if (!fichier.type.startsWith('image/')) {
      message.error('Seules les images sont acceptées.');
      return false;
    }
    if (fichier.size > 5 * 1024 * 1024) {
      message.error('Image trop lourde : 5 Mo maximum.');
      return false;
    }

    const lecteur = new FileReader();
    lecteur.onload = () => {
      const reference = { type: 'data_url', data: lecteur.result };
      setImagesReference(precedentes => [reference, ...precedentes]);
      // Sélectionnée d'office : c'est le geste attendu après un téléversement
      setReferenceChoisie(reference);
      message.success('Image de référence ajoutée');
    };
    lecteur.readAsDataURL(fichier);

    return false;
  };

  /** Lance la génération des visuels auprès du modèle choisi */
  const genererImages = async () => {
    if (!prompt.trim()) {
      message.warning('Le prompt est vide.');
      return;
    }

    try {
      setGeneration(true);
      setResultats([]);
      setSelection([]);

      const { data } = await productsApi.generateImage({
        prompt,
        // Image de référence : le modèle s'appuie dessus pour rester fidèle au produit.
        // Déjà au format { type, data } attendu par le service (url ou data_url).
        images: referenceChoisie ? [referenceChoisie] : [],
        model,
        numberOfImages: nombreImages,
        quality,
        aspectRatio
      });

      setResultats(data.imageUrls || []);
      if (!data.imageUrls?.length) message.warning('Aucune image générée, reformulez le prompt.');
    } catch (error) {
      console.error('Erreur lors de la génération des visuels:', error);
      message.error(error.response?.data?.message || 'Échec de la génération');
    } finally {
      setGeneration(false);
    }
  };

  /**
   * Ajoute ou retire un visuel de la sélection.
   * L'ordre est conservé : le premier sélectionné est celui appliqué à l'annonce.
   *
   * @param {string} image - Image générée, encodée en base64
   */
  const basculerSelection = (image) => {
    setSelection(precedente =>
      precedente.includes(image)
        ? precedente.filter(i => i !== image)
        : [...precedente, image]
    );
  };

  /**
   * Enregistre tous les visuels sélectionnés et applique le premier à l'annonce.
   *
   * Les autres sont conservés dans la bibliothèque du produit : ils restent
   * disponibles pour les autres annonces, les campagnes suivantes et le catalogue.
   */
  const enregistrerSelection = async () => {
    if (selection.length === 0) return;

    try {
      setEnregistrement(true);

      // Enregistrement séquentiel : l'ordre détermine quel visuel revient à l'annonce
      const urls = [];
      for (const image of selection) {
        const { data } = await productsApi.saveGeneratedImage({
          prompt,
          imageUrl: image,
          model,
          productId: productId || null
        });

        const urlPublique = data?.image_url || data?.data?.image_url;
        if (urlPublique) urls.push(urlPublique);
      }

      if (urls.length === 0) throw new Error('Aucune URL publique renvoyée');

      onSelect(urls[0]);
      message.success(
        urls.length > 1
          ? `${urls.length} visuels enregistrés — le premier est appliqué à l'annonce`
          : "Visuel appliqué à l'annonce"
      );
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement des visuels:', error);
      message.error('Impossible d\'enregistrer les visuels');
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <PictureOutlined />
          <span>Visuel de l'annonce</span>
          {ad?.angle && <Tag color="blue" className="private-tag-accent" >{ad.angle}</Tag>}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
    >
      <Tabs
        activeKey={onglet}
        onChange={setOnglet}
        items={[
          {
            key: 'bibliotheque',
            label: 'Visuels existants',
            children: (
              <BibliothequeVisuels
                productId={productId}
                imageActuelle={ad?.imageUrl}
                onSelect={(url) => { onSelect(url); message.success("Visuel appliqué à l'annonce"); onClose(); }}
              />
            )
          },
          {
            key: 'composer',
            label: 'Importer ou composer',
            children: (
              <EditeurCompositionAnnonce
                productId={productId}
                imagesInitiales={selection}
                onSelect={onSelect}
                onClose={onClose}
              />
            )
          },
          {
            key: 'generer',
            label: 'Générer un nouveau visuel',
            children: (
      <div className="space-y-4">
        <Alert
          type="info"
          showIcon
          message={prompt ? "Prompt du visuel de cette annonce" : "Aucun prompt pour le moment"}
          description={
            prompt
              ? "Modifiez-le librement, ou demandez une reformulation en précisant la direction souhaitée."
              : "Cliquez sur Rédiger pour que l'IA en propose un à partir de l'angle de l'annonce. Vous pouvez d'abord saisir une consigne pour orienter la mise en scène."
          }
        />

        <div>
          <label className="block text-sm font-medium mb-1">Prompt (en anglais)</label>

          {/* Orienter la reformulation : la consigne prime sur les choix de l'IA */}
          <Space.Compact className="w-full mb-2">
            <Input
              value={directive}
              onChange={e => setDirective(e.target.value)}
              onPressEnter={() => redigerPrompt(directive)}
              placeholder="Orienter le visuel (optionnel) : ex. en extérieur, femme de 30 ans, ambiance du soir…"
              allowClear
            />
            <Button
              type="primary"
              ghost
              loading={redactionPrompt}
              onClick={() => redigerPrompt(directive)}
              icon={<ReloadOutlined />}
            >
              {prompt ? 'Reformuler' : 'Rédiger'}
            </Button>
          </Space.Compact>
          <TextArea
            rows={6}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder={redactionPrompt ? "Rédaction du prompt en cours…" : "Décrivez la scène du visuel publicitaire"}
          />

          {/* Mises en scène alternatives proposées par l'IA */}
          {propositions.length > 1 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              <span className="text-xs text-gray-500">Autres mises en scène :</span>
              {propositions.map((p, i) => (
                <Tag
                  key={i}
                  color={prompt === p.prompt ? 'blue' : 'default'}
                  className="cursor-pointer"
                  onClick={() => setPrompt(p.prompt)}
                >
                  {p.focus || `Variante ${i + 1}`}
                </Tag>
              ))}
            </div>
          )}
        </div>

        {/* Image de référence : garde le visuel fidèle au produit réel */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Image de référence (optionnel)
          </label>

          <div className="flex gap-2 flex-wrap items-center">
            {imagesReference.slice(0, 12).map((reference, i) => {
              const selectionnee = referenceChoisie?.data === reference.data;
              return (
                <div key={reference.data.slice(0, 60) + i} className="relative group">
                  <img
                    src={reference.data}
                    alt="Référence"
                    onClick={() => setReferenceChoisie(selectionnee ? null : reference)}
                    className={`w-16 h-16 object-cover rounded cursor-pointer border-2 ${
                      selectionnee ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                    }`}
                  />
                  {/* Agrandissement, sans déclencher la sélection */}
                  <div
                    className="absolute top-0 right-0 bg-black bg-opacity-60 rounded-bl px-1 opacity-0 group-hover:opacity-100 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); setApercu(reference.data); }}
                  >
                    <EyeOutlined className="text-white text-xs" />
                  </div>
                  {/* Une image téléversée se distingue de celles du produit */}
                  {reference.type === 'data_url' && (
                    <span className="absolute bottom-0 left-0 bg-blue-600 text-white text-[9px] px-1 rounded-tr">
                      importée
                    </span>
                  )}
                </div>
              );
            })}

            <Upload beforeUpload={televerserReference} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />} className="h-16">
                Importer
              </Button>
            </Upload>
          </div>

          <p className="text-xs text-gray-500 mt-1">
            Le modèle s'appuie sur cette image pour rester fidèle au produit. 5 Mo maximum.
          </p>
        </div>

        <Space wrap>
          <div>
            <label className="block text-sm font-medium mb-1">Modèle</label>
            <Select
              value={model}
              onChange={setModel}
              options={MODELES_IMAGE}
              style={{ width: 300 }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Format</label>
            {/* Liste filtrée : tous les modèles n'acceptent pas les mêmes ratios */}
            <Select
              value={aspectRatio}
              onChange={setAspectRatio}
              options={formatsPourModele(model)}
              style={{ width: 240 }}
            />
          </div>

          {/* La qualité n'est prise en compte que par GPT Image 2.5 Sunburst */}
          {model === MODELE_GPT && (
            <div>
              <label className="block text-sm font-medium mb-1">Qualité</label>
              <Radio.Group value={quality} onChange={e => setQuality(e.target.value)} buttonStyle="solid">
                <Radio.Button value="low">Basse</Radio.Button>
                <Radio.Button value="medium">Moyenne</Radio.Button>
                <Radio.Button value="high">Haute</Radio.Button>
              </Radio.Group>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Nombre</label>
            <InputNumber min={1} max={4} value={nombreImages} onChange={setNombreImages} />
          </div>

          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={generation}
            onClick={genererImages}
            style={{ marginTop: 22 }}
          >
            Générer
          </Button>
        </Space>

        {generation && (
          <div className="text-center py-8">
            <Spin tip="Génération en cours…" />
          </div>
        )}

        {/* Résultats : sélection multiple, puis enregistrement groupé */}
        {resultats.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-2">
              Sélectionnez les visuels à conserver — le premier sera appliqué à cette annonce
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {resultats.map((image, i) => {
                // Rang dans la sélection : le premier choisi devient le visuel de l'annonce
                const rang = selection.indexOf(image);
                const choisie = rang !== -1;

                return (
                  <div
                    key={i}
                    className={`relative rounded overflow-hidden border-2 cursor-pointer group transition ${
                      choisie ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => basculerSelection(image)}
                  >
                    <img src={image} alt={`Proposition ${i + 1}`} className="w-full h-auto object-contain bg-gray-100" />

                    {/* Agrandir : au-dessus du reste, et n'affecte pas la sélection */}
                    <div
                      className="absolute top-1 right-1 z-20 bg-black bg-opacity-60 rounded p-1 opacity-0 group-hover:opacity-100 cursor-pointer transition"
                      onClick={(e) => { e.stopPropagation(); setApercu(image); }}
                      title="Voir en grand"
                    >
                      <EyeOutlined className="text-white" />
                    </div>

                    {/* Rang de sélection, ou pastille vide au survol */}
                    <div
                      className={`absolute top-1 left-1 z-10 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition ${
                        choisie
                          ? 'bg-blue-600 text-white'
                          : 'bg-white bg-opacity-80 text-gray-400 opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      {choisie ? rang + 1 : <CheckOutlined />}
                    </div>

                    {/* Visuel appliqué à l'annonce */}
                    {rang === 0 && (
                      <span className="absolute bottom-0 inset-x-0 z-10 bg-blue-600 text-white text-[10px] text-center py-0.5">
                        appliqué à l'annonce
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-3 flex items-center gap-3">
              <Button
                icon={<PictureOutlined />}
                disabled={selection.length < 2}
                onClick={() => setOnglet('composer')}
              >
                Composer les visuels sélectionnés
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={enregistrement}
                disabled={selection.length === 0}
                onClick={enregistrerSelection}
              >
                {selection.length > 1
                  ? `Enregistrer les ${selection.length} visuels`
                  : 'Enregistrer le visuel'}
              </Button>
              <span className="text-xs text-gray-500">
                Les visuels non appliqués rejoignent la bibliothèque du produit et resteront
                disponibles pour vos autres annonces.
              </span>
            </div>
          </div>
        )}
      </div>
            )
          }
        ]}
      />

      {/* Aperçu en grand : composant piloté, aucune vignette affichée dans le flux */}
      {apercu && (
        <Image
          src={apercu}
          style={{ display: 'none' }}
          preview={{
            visible: true,
            src: apercu,
            onVisibleChange: (visible) => { if (!visible) setApercu(null); }
          }}
        />
      )}
    </Modal>
  );
};

export default GenerateurImageAnnonce;
